import { motion } from "framer-motion";
import { VevePattern } from "./VevePattern";

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.15, delayChildren: 0.3 },
    },
};

const letterVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
    },
};

const fadeUpVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
    },
};

export function Hero() {
    const title = "VUDO";

    return (
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
            <motion.div
                className="relative z-10 text-center px-6 max-w-4xl mx-auto"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="flex justify-center items-center gap-2 sm:gap-3 md:gap-6 lg:gap-8 mb-6">
                    {title.split("").map((letter, i) => (
                        <motion.span
                            key={i}
                            variants={letterVariants}
                            className="text-4xl sm:text-5xl md:text-7xl lg:text-9xl font-light tracking-[0.1em] sm:tracking-[0.2em] md:tracking-[0.3em] text-transparent bg-clip-text bg-gradient-to-b from-white to-white/60"
                            style={{
                                fontFamily: "'Cinzel Decorative', Cinzel, serif",
                                textShadow: "0 0 30px rgba(232, 194, 90, 0.3)",
                            }}
                        >
                            {letter}
                        </motion.span>
                    ))}
                </div>

                <motion.p
                    variants={fadeUpVariants}
                    className="text-sm md:text-base tracking-[0.4em] uppercase text-[#e8c25a]/80 mb-12"
                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                >
                    Virtual Univrs Design Ontology
                </motion.p>

                <motion.div
                    variants={fadeUpVariants}
                    className="w-48 h-px bg-gradient-to-r from-transparent via-[#f4d77c] to-transparent mx-auto mb-12"
                />

                <motion.h2
                    variants={fadeUpVariants}
                    className="text-xl md:text-2xl lg:text-3xl text-white/90 mb-2 font-light"
                >
                    Imagine. Summon. Create.
                </motion.h2>

                <motion.p
                    variants={fadeUpVariants}
                    className="text-lg md:text-xl lg:text-2xl text-[#e8c25a] mb-4 max-w-xl mx-auto font-light italic"
                >
                    Make the Univrs in your image.
                </motion.p>

                <motion.p
                    variants={fadeUpVariants}
                    className="text-base md:text-lg text-[#f4d77c]/80 mb-12 max-w-xl mx-auto font-light"
                >
                    Le réseau est Bondieu
                </motion.p>

                <motion.div
                    variants={fadeUpVariants}
                    className="flex justify-center mb-12"
                >
                    <div className="md:hidden">
                        <VevePattern type="mitan" size={80} delay={1.5} />
                    </div>
                    <div className="hidden md:block">
                        <VevePattern type="mitan" size={120} delay={1.5} />
                    </div>
                </motion.div>

                <motion.div
                    variants={fadeUpVariants}
                    className="flex flex-col sm:flex-row items-center justify-center gap-4"
                >
                    <a
                        href="#imaginarium"
                        className="group relative px-8 py-4 bg-[#e8c25a]/10 border border-[#e8c25a]/30 rounded-sm text-[#e8c25a] hover:bg-[#e8c25a]/20 hover:border-[#e8c25a]/50 transition-all duration-300"
                    >
                        <span className="relative z-10 tracking-wider uppercase text-sm">
                            Enter the Imaginarium
                        </span>
                    </a>

                    <a
                        href="https://github.com/univrs/dol"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-8 py-4 text-white/60 hover:text-white/90 transition-colors duration-300 tracking-wider uppercase text-sm"
                    >
                        View Source →
                    </a>
                </motion.div>

                <motion.div
                    className="absolute bottom-12 left-1/2 -translate-x-1/2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2.5, duration: 1 }}
                >
                    <motion.div
                        animate={{ y: [0, 8, 0] }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                        className="w-6 h-10 border border-white/20 rounded-full flex justify-center"
                    >
                        <motion.div
                            animate={{ y: [0, 12, 0], opacity: [1, 0.3, 1] }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                            className="w-1 h-2 bg-white/40 rounded-full mt-2"
                        />
                    </motion.div>
                </motion.div>
            </motion.div>
        </section>
    );
}

export default Hero;
